/**
 * theme-customization A3 (+ B2's account preference) — the AUTHENTICATED PORTAL THEME RUNTIME.
 *
 * Mounted by `PortalLayout`, BELOW `AuthGuard`/`RoleGuard` and not in global `Providers`: the
 * palette is authenticated per-user server state, so the public routes (login, register, magic
 * landing, the branded magic-link shells, the public report, 404) must be able to render without
 * it and must make NO theme request. Mounting it globally would make every one of those routes
 * depend on an authenticated GET.
 *
 * WHAT THIS OWNS
 *   · Bootstrap: on cold load the portal is not revealed at all until an authenticated,
 *     schema-valid `GET /theme` comes back with `ownerUserId` equal to the signed-in user.
 *     Until then a fixed neutral surface renders, with Sign out available immediately.
 *   · Application: the complete resolved palette goes onto `documentElement` in a LAYOUT effect,
 *     so it is in place before the first painted frame of the portal, and the exact revert runs
 *     on teardown — the theme is gone before any public route paints.
 *   · Ownership: every asynchronous result is compared against the tuple it STARTED under
 *     (owner user id, session generation, provider lifetime) before it touches the DOM, the
 *     query cache or auth. The query key carries the same tuple, so a new owner cannot read a
 *     previous owner's cached snapshot or reuse a previous ready flag while its own load runs.
 *   · Deadlines: every theme operation has a 10-second UI budget. At the deadline the operation
 *     is marked SETTLED and its transport aborted, so a late same-owner response can no longer
 *     update anything. A read timeout keeps the last confirmed palette; a write timeout means
 *     OUTCOME UNKNOWN and says so.
 *   · The scoped toaster: while the portal is themed, the global toaster stands down and one
 *     toaster renders here, painted from theme surface/text/border/status roles.
 *
 * WHAT B2 ADDS
 *   · PROTOCOL VERSION 2. Both the snapshot GET and the admin PUT send `?clientVersion=2`, so the
 *     server answers with the caller's STORED preference and the complete capability set. A
 *     cached delivery-A tab keeps sending version 1 and keeps getting light-only answers.
 *   · THE ACCOUNT PREFERENCE. `setPreference` is a real, SERIALIZED writer: overlapping choices
 *     cannot interleave or land out of order, the last requested one wins, a failure rolls the
 *     painted mode back to the confirmed server value, and a `GET /theme` that was in flight when
 *     a preference committed can no longer overwrite the newer value.
 *
 * WHAT THIS DOES NOT DO: no theme `localStorage`/`sessionStorage`, no persisted query cache, no
 * automatic retry of a write, and no second source of truth for the preference — the confirmed
 * value lives in the query cache and the only other copy is the short-lived optimistic overlay
 * this file owns, which is dropped the moment the server confirms or refuses.
 */

import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import {
  STOCK_COLOURS,
  resolveMode,
  resolveTheme,
  themePreferenceSchema,
  themeSnapshotSchema,
  type PreferenceResult,
  type ThemeMode,
  type ThemePreference,
  type ThemeSaveInput,
  type ThemeSaveResult,
  type ThemeSnapshot,
} from '@socialsoup/shared';
import { ApiRequestError, api } from '@/lib/api';
import { applyOwnedTheme, portalPrintCss } from '@/lib/theme';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAuthStore } from '@/stores/authStore';
import { ToastLiveRegion, useClaimScopedToaster } from '@/app/providers';

// ─── The frontend seam ───────────────────────────────────────────────────────

export type PortalThemeContext = {
  snapshot: ThemeSnapshot; // Includes preference and supportedModes.
  mode: ThemeMode;
  reload: () => Promise<void>;
  saveTheme: (input: ThemeSaveInput) => Promise<ThemeSaveResult>;
  preferenceSave: { pending: boolean; error: string | null };
  setPreference: ((preference: ThemePreference) => Promise<void>) | null;
};

const ThemeContext = React.createContext<PortalThemeContext | null>(null);

export function useTheme(): PortalThemeContext {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used inside <ThemeProvider> (it mounts in PortalLayout).');
  }
  return context;
}

/**
 * The same seam for a component that must render WITHOUT the runtime as well as inside it.
 *
 * `TopNavBar` (B2's preference picker) is the one such consumer: in the application it is always a
 * descendant of this provider, but it is also rendered standalone by several existing suites
 * (`components/__tests__/portal-contrast.test.tsx`, `test/__tests__/axe-portals.phase14.test.tsx`)
 * which are about the navigation chrome and know nothing about themes. `useTheme`'s throw is the
 * right default for the editor — a mounting mistake there should be loud — but it would turn those
 * unrelated suites red, so the shared nav asks for the context instead of demanding it, and simply
 * renders no picker when there is none.
 */
export function useOptionalTheme(): PortalThemeContext | null {
  return React.useContext(ThemeContext);
}

// ─── Deadlines and failure kinds ─────────────────────────────────────────────

/**
 * An explicit UI WAIT BUDGET for these small settings requests — not a global upload timeout.
 * It covers the whole operation INCLUDING any token refresh the request triggers, because what
 * is being bounded is how long a user stares at a gated portal.
 */
export const THEME_DEADLINE_MS = 10_000;

export const THEME_TIMEOUT = 'THEME_TIMEOUT';
export const THEME_INVALID = 'THEME_INVALID';
export const THEME_OWNER_MISMATCH = 'THEME_OWNER_MISMATCH';
export const THEME_NOT_OWNED = 'THEME_NOT_OWNED';
/** The provider was torn down while this operation was in flight. */
export const THEME_ABANDONED = 'THEME_ABANDONED';

export class ThemeError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ThemeError';
  }
}

/**
 * What the preference picker announces when a write does not land.
 *
 * A TIMEOUT IS NOT A FAILURE (the same rule the editor follows for a save): the write may have
 * committed, so the copy says the outcome is unknown and never that nothing was saved. The
 * runtime re-reads the server state; it does not retry the write.
 */
function describePreferenceError(error: unknown): string {
  if (error instanceof ThemeError && error.code === THEME_TIMEOUT) {
    return 'We could not confirm your appearance choice in time. Checking what the server has.';
  }
  if (error instanceof ApiRequestError) return error.message;
  return error instanceof Error ? error.message : 'Your appearance choice could not be saved.';
}

/** One theme operation's liveness. Set once, at its deadline or at its settlement. */
type Operation = { settled: boolean };

/**
 * Run `work` under the deadline.
 *
 * Marking the operation SETTLED at the deadline is the part that matters and the part an abort
 * cannot do on its own: `AbortController` is best effort (a mocked transport, a response already
 * buffered, or a fetch that ignores the signal all still resolve), so the flag — checked by
 * every continuation below — is what actually stops a late same-owner response from updating
 * query data, the DOM or mutation state.
 */
async function withDeadline<T>(
  operation: Operation,
  registry: Set<() => void>,
  work: (signal: AbortSignal) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let abandon: ((error: ThemeError) => void) | null = null;

  const promise = new Promise<T>((resolve, reject) => {
    abandon = reject;
    timer = setTimeout(() => {
      if (operation.settled) return;
      operation.settled = true;
      controller.abort();
      reject(new ThemeError(THEME_TIMEOUT, 'The theme request took too long.'));
    }, THEME_DEADLINE_MS);
    work(controller.signal).then(
      (value) => {
        if (operation.settled) return;
        operation.settled = true;
        resolve(value);
      },
      (error: unknown) => {
        if (operation.settled) return;
        operation.settled = true;
        reject(error);
      },
    );
  });

  /**
   * TEARDOWN CANCELS THE TRANSPORT, THE TIMER AND THE AWAIT (A3 review, item 6b).
   *
   * Removing the query entry on unmount left the fetch running and the deadline armed: the
   * abandoned request could still reach the 401 path (and, before this batch, redirect a PUBLIC
   * route to `/login`), and its timer still fired into a dead provider. Rejecting as well as
   * aborting matters because abort is best effort — without it the awaiting caller would hang
   * forever on a promise neither continuation is allowed to settle.
   */
  const cancel = (): void => {
    if (operation.settled) return;
    operation.settled = true;
    controller.abort();
    clearTimeout(timer);
    abandon?.(new ThemeError(THEME_ABANDONED, 'The theme operation was abandoned on teardown.'));
  };
  registry.add(cancel);

  try {
    return await promise;
  } finally {
    clearTimeout(timer);
    registry.delete(cancel);
  }
}

// ─── Ownership ───────────────────────────────────────────────────────────────

/** Unique per provider MOUNT. A remount is a different lifetime and shares no readiness. */
let lifetimeCounter = 0;

/** Marks the injected portal print rule so tests and teardown can find exactly that element. */
export const PRINT_STYLE_ATTRIBUTE = 'data-portal-print';

type ThemeOwner = { userId: string; generation: number; lifetime: number };

// ─── Fixed neutral bootstrap surface ─────────────────────────────────────────

/**
 * The bootstrap surface paints from STOCK LIGHT, read from the shared catalogue rather than
 * from the live custom properties. That is what "fixed neutral" has to mean here: this surface
 * renders exactly when no validated palette is in force, so it must not depend on whatever
 * values happen to be sitting on the document root.
 */
const NEUTRAL = STOCK_COLOURS.light;

const neutralSurface: React.CSSProperties = {
  backgroundColor: NEUTRAL['--bg-light'],
  color: NEUTRAL['--text-primary'],
};

const neutralPanel: React.CSSProperties = {
  backgroundColor: NEUTRAL['--bg-card'],
  color: NEUTRAL['--text-primary'],
  borderColor: NEUTRAL['--brand-border'],
};

const neutralAction: React.CSSProperties = {
  borderColor: NEUTRAL['--input'],
  color: NEUTRAL['--text-primary'],
};

/** 44px on coarse pointers (2.5.5), and a visible focus ring from the stock interactive colour. */
const ACTION_CLASS =
  'inline-flex items-center justify-center rounded-md border px-4 text-sm font-medium ' +
  'min-h-[36px] [@media(pointer:coarse)]:min-h-[44px] [@media(pointer:coarse)]:min-w-[44px]';

/**
 * sonner paints from CSS variables it declares on its own `<ol>`; an inline style on that element
 * wins, so each one is re-pointed at a theme role and follows an admin's palette live. The
 * surface/text/border roles are editable; the status roles are code-owned, and each pair here is
 * one the contrast registry already measures as a status badge.
 */
const THEME_TOAST_STYLE = {
  '--normal-bg': 'var(--bg-card)',
  '--normal-bg-hover': 'var(--bg-surface-light)',
  '--normal-border': 'var(--brand-border)',
  '--normal-border-hover': 'var(--brand-border)',
  '--normal-text': 'var(--text-primary)',
  // What sonner's LIGHT close button reads.
  '--gray1': 'var(--bg-card)',
  '--gray4': 'var(--brand-border)',
  '--gray12': 'var(--text-primary)',
  '--success-bg': 'var(--status-approved-bg)',
  '--success-border': 'var(--status-approved-text)',
  '--success-text': 'var(--status-approved-text)',
  '--warning-bg': 'var(--status-pending-bg)',
  '--warning-border': 'var(--status-pending-text)',
  '--warning-text': 'var(--status-pending-text)',
  '--error-bg': 'var(--status-rejected-bg)',
  '--error-border': 'var(--status-rejected-text)',
  '--error-text': 'var(--status-rejected-text)',
  '--info-bg': 'var(--status-review-bg)',
  '--info-border': 'var(--status-review-text)',
  '--info-text': 'var(--status-review-text)',
} as React.CSSProperties;

/**
 * The portal's own toaster. While it is mounted the global one stands down through the EXISTING
 * claim (`useClaimScopedToaster`), so every toast renders and is announced exactly once. It is
 * mounted only once the portal is revealed — during bootstrap the global toaster is still the
 * one in charge, which is what keeps a sign-in error visible on the way in.
 */
function PortalToaster() {
  useClaimScopedToaster();
  return (
    <ToastLiveRegion>
      <Toaster position="top-right" richColors closeButton style={THEME_TOAST_STYLE} />
    </ToastLiveRegion>
  );
}

// ─── The provider ────────────────────────────────────────────────────────────

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { logout } = useAuth();
  const userId = useAuthStore((state) => state.user?.id) ?? null;
  const generation = useAuthStore((state) => state.sessionGeneration);
  const clearAuth = useAuthStore((state) => state.clearAuth);

  const [lifetime] = React.useState(() => {
    lifetimeCounter += 1;
    return lifetimeCounter;
  });

  // Live while THIS provider instance is mounted. A callback that settles after teardown owns
  // nothing, however well its user id and generation still compare.
  const activeRef = React.useRef(true);
  /** Cancel handles for every theme operation this lifetime still has in flight. */
  const inFlightRef = React.useRef<Set<() => void>>(new Set());
  React.useEffect(() => {
    activeRef.current = true;
    const inFlight = inFlightRef.current;
    return () => {
      activeRef.current = false;
      // Abort the transports and disarm the deadlines before the query entries go: a route
      // change to a PUBLIC page must leave nothing of this lifetime still talking to the API.
      for (const cancel of [...inFlight]) cancel();
      inFlight.clear();
    };
  }, []);

  const owns = React.useCallback(
    (owner: ThemeOwner): boolean => {
      if (!activeRef.current) return false;
      if (owner.lifetime !== lifetime) return false;
      const state = useAuthStore.getState();
      return owner.userId === state.user?.id && owner.generation === state.sessionGeneration;
    },
    [lifetime],
  );

  const currentOwner = React.useCallback(
    (): ThemeOwner => ({ userId: userId ?? '', generation, lifetime }),
    [userId, generation, lifetime],
  );

  // READINESS IS KEYED TO THE WHOLE TUPLE. A new owner, a new sign-in or a remount is a
  // different key, so `data` is `undefined` again and the portal is gated again — there is no
  // placeholder data and no carried-over ready flag by construction.
  const queryKey = React.useMemo(
    () => ['theme', userId, generation, lifetime] as const,
    [userId, generation, lifetime],
  );

  // ── The preference writer's owned state (B2) ──────────────────────────────
  // Declared before the query because `queryFn` reads two of these refs: the suppression rule
  // for a read that was in flight when a preference committed lives inside the read itself.

  /** The OPTIMISTIC preference. Painted while a PATCH is in flight; dropped on confirmation and
   *  on failure, so no optimistic value can survive a failed write. */
  const [pendingPreference, setPendingPreference] = React.useState<ThemePreference | null>(null);
  const [preferencePending, setPreferencePending] = React.useState(false);
  /**
   * OWNED, for the reason the query's `warmError` is owned (measured on the installed
   * `@tanstack/react-query@5.90.20`): a result object read off a hook can both miss a failure and
   * flash it later. A preference failure is announced from this state or not at all.
   */
  const [preferenceError, setPreferenceError] = React.useState<string | null>(null);

  /** The tail of the serialized write chain. One PATCH at a time, in request order. */
  const preferenceQueueRef = React.useRef<Promise<void>>(Promise.resolve());
  /** Monotonic per request. Only the LATEST request may move the painted value or the controls. */
  const preferenceRequestRef = React.useRef(0);
  /** Monotonic per COMMITTED write. A read that captured an older value is stale by definition. */
  const preferenceCommitRef = React.useRef(0);
  /** The last preference the server confirmed to this provider lifetime. */
  const confirmedPreferenceRef = React.useRef<ThemePreference | null>(null);

  const themeQuery = useQuery({
    queryKey,
    enabled: userId !== null,
    retry: false,
    // No persistent cache for the palette: it is re-read per session, never carried.
    gcTime: 0,
    /**
     * NO UN-OWNED FETCH (A3 review, item 7). Every fetch of this query goes through `refresh()`
     * or the initial mount, both of which record the outcome in owned state. Window-focus and
     * RECONNECT refetching are off because their failures would be recorded nowhere: measured on
     * the installed `@tanstack/react-query@5.90.20`, a failed refetch with data present does not
     * notify the observer at all (no re-render), yet the SAME result object reads
     * `status: 'error'` the next time anything else re-renders — so a hook-derived banner both
     * misses the failure and then flashes it at an unrelated moment. Explicit reload is the
     * reconciliation path this plan specifies anyway.
     */
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    /**
     * `networkMode: 'always'` (A3 review, item 6a). The default `'online'` PAUSES an operation
     * before `queryFn` runs when the browser reports itself offline, so the 10-second deadline
     * never starts and the portal sits on its loading surface indefinitely. The deadline is this
     * feature's bound on waiting; a paused operation with no deadline is exactly the silent hang
     * it exists to prevent. Offline now fails fast through the ordinary failure surfaces.
     */
    networkMode: 'always',
    queryFn: async (): Promise<ThemeSnapshot> => {
      const owner = currentOwner();
      // Captured BEFORE the request goes out, so the comparison after it is "did a preference
      // commit while I was in flight?" — see `writePreference` below.
      const commitAtStart = preferenceCommitRef.current;
      const operation: Operation = { settled: false };
      const response = await withDeadline(operation, inFlightRef.current, (signal) =>
        api.get<unknown>('/api/v1/theme?clientVersion=2', {
          signal,
          // A late 401 from an ABANDONED bootstrap must not refresh, clear auth, or navigate a
          // public route to /login — the provider that asked for it is gone.
          callerActive: () => activeRef.current,
        }),
      );
      const parsed = themeSnapshotSchema.safeParse(response.data);
      if (!parsed.success) {
        throw new ThemeError(THEME_INVALID, 'The theme response did not match its schema.');
      }
      if (parsed.data.ownerUserId !== owner.userId) {
        throw new ThemeError(THEME_OWNER_MISMATCH, 'The theme response belongs to another user.');
      }
      /**
       * STALE-READ SUPPRESSION (the plan's least-confident B2 line).
       *
       * A read that started before a preference committed carries the PRE-WRITE preference, and
       * it would otherwise become the query's data and repaint the portal in the mode the user
       * just moved away from. Only the preference is substituted: the rest of this response
       * (overrides, revision) is genuinely fresher than what the cache holds, and the value
       * substituted in is itself server state — the `PATCH` echo, which is newer than this read.
       *
       * `cancelQueries` before each write is the first line of defence; this is the one that does
       * not depend on an abort landing in time.
       */
      const confirmed = confirmedPreferenceRef.current;
      if (confirmed !== null && preferenceCommitRef.current !== commitAtStart) {
        return { ...parsed.data, preference: confirmed };
      }
      return parsed.data;
    },
  });

  const snapshot = themeQuery.data;

  /**
   * THE PRE-SNAPSHOT DEFAULT IS `SYSTEM`, and its only job is to be safe: the shell does not
   * render before the snapshot arrives (`value` is null, so the neutral bootstrap surface paints
   * from stock-light inline styles and the layout effect applies nothing to the document).
   *
   * `SYSTEM` rather than A's `LIGHT` for two reasons. It is the server's own column default
   * (`User.themePreference @default(SYSTEM)`), so the placeholder agrees with the value most
   * accounts really hold instead of asserting a choice they never made. And because the branch
   * below only listens while the preference is `SYSTEM`, defaulting to it means the OS state is
   * already being tracked when the snapshot lands, rather than being discovered one render later
   * — which is the difference between revealing a dark portal and revealing a light one and then
   * repainting it.
   */
  const storedPreference: ThemePreference = snapshot?.preference ?? 'SYSTEM';
  /** What the runtime is PAINTING: the optimistic choice while a PATCH is in flight, else the
   * confirmed server value. Never a second stored copy — see `writePreference`. */
  const preference: ThemePreference = pendingPreference ?? storedPreference;

  // ── System resolution ─────────────────────────────────────────────────────
  // Resolved from the CURRENT matchMedia and kept live only while the preference is SYSTEM; an
  // explicit LIGHT/DARK ignores OS changes, which is the whole difference between the three.
  //
  // WHAT IS MEASURED IS "THE FIRST `color-scheme` WRITTEN IS THE RIGHT ONE" — not this
  // initializer. Two things independently hold that, and the QA mutation pass established which:
  // breaking the initializer ALONE escapes both the jsdom test and the real-Chromium one, because
  // the pre-snapshot `SYSTEM` default (above) attaches the effect below during bootstrap and its
  // re-sync therefore runs BEFORE the snapshot — and so before the first application — lands.
  // Breaking both reddens. The eager read is kept as defensive depth: it is what would hold the
  // claim if that default ever moved off `SYSTEM`. Do not describe it as the load-bearing half.
  //
  // `addEventListener` is used without an `addListener` fallback deliberately — every browser
  // this bundle targets (and Playwright's Chromium) has supported it on `MediaQueryList` since
  // 2018, and a fallback nothing needs is dead code.
  const [systemDark, setSystemDark] = React.useState(
    () => window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false,
  );
  React.useEffect(() => {
    if (preference !== 'SYSTEM') return;
    const query = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!query) return;
    // Re-sync on attach: while an explicit mode was in force there was no listener, so the OS may
    // have moved without this provider hearing about it.
    setSystemDark(query.matches);
    const onChange = (event: MediaQueryListEvent): void => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [preference]);

  const mode = resolveMode(preference, systemDark);

  // ── Owned DOM application ─────────────────────────────────────────────────
  // A LAYOUT effect: the variables are on the root before the browser paints the revealed
  // portal, and the cleanup's revert runs before any public route paints.
  React.useLayoutEffect(() => {
    if (!snapshot) return;
    return applyOwnedTheme(document.documentElement, resolveTheme(mode, snapshot.overrides), mode);
  }, [snapshot, mode]);

  // ── The portal print rule ─────────────────────────────────────────────────
  // Unconditional, and it is the FIRST thing the shell installs: the stock-light block has to be
  // in the document before anything paints (or prints), independently of whether this lifetime's
  // palette has arrived. Removed with the shell, so a public route carries no portal print rule.
  React.useLayoutEffect(() => {
    const style = document.createElement('style');
    style.setAttribute(PRINT_STYLE_ATTRIBUTE, '');
    style.textContent = portalPrintCss();
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  // ── Teardown: this lifetime's query state and nothing else ────────────────
  React.useEffect(() => {
    return () => {
      queryClient.removeQueries({
        queryKey: ['theme'],
        predicate: (query) => query.queryKey[3] === lifetime,
      });
    };
  }, [queryClient, lifetime]);

  // ── Warm-failure state and the owned refresh ──────────────────────────────
  /**
   * THE WARM FAILURE IS OWNED STATE, and it has to be — MEASURED, not assumed.
   *
   * TanStack Query v5 keeps the OBSERVER's result on the last success while cached data is still
   * being shown: after a failed `refetch()` with data present, the rendered result reads
   * `status: 'success'`, `isError: false`, `error: null` and even `errorUpdateCount: 0`, while
   * the underlying query STATE holds `status: 'error'`. The error is returned by `refetch()`
   * itself. So reading the hook's fields would have made "Warm network/5xx → one nonblocking
   * error" silently never fire; the refetch's own return value is the honest source.
   *
   * Owning it also gives the ownership comparison somewhere to bite: a refetch that settles
   * after the account changed announces nothing, because it rolls back only a matching
   * owner/generation/lifetime.
   */
  const [warmError, setWarmError] = React.useState<Error | null>(null);

  const refetch = themeQuery.refetch;
  const refresh = React.useCallback(async (): Promise<void> => {
    const owner = currentOwner();
    const result = await refetch();
    if (!owns(owner)) return;
    setWarmError(result.isError ? ((result.error as Error | null) ?? null) : null);
  }, [refetch, owns, currentOwner]);

  // A new owner/generation/lifetime is a different query key and a different bootstrap; the
  // previous lifetime's warm error must not describe it.
  React.useEffect(() => {
    setWarmError(null);
  }, [queryKey]);

  const coldError = (themeQuery.error as ThemeError | Error | null) ?? null;
  const activeError = coldError ?? warmError;
  const errorCode = activeError instanceof ThemeError ? activeError.code : null;
  const coldFailed = snapshot === undefined && coldError !== null;

  // ── Invalid response / owner mismatch: clear only the CAPTURED session ────
  const recoveredRef = React.useRef(false);
  React.useEffect(() => {
    /**
     * ONLY AN OWNER MISMATCH ENDS THE SESSION (A3 review, item 3).
     *
     * `THEME_INVALID` used to land here too. It is a STRICT-SCHEMA parse failure, and the
     * realistic trigger is API/bundle version skew - an additive field on the snapshot would
     * have signed out every open tab of every portal. It is a failure, not a compromised
     * session: `coldFailed` routes it to the neutral surface with Retry and Sign out, which is
     * what a version skew deserves. A mismatched `ownerUserId` is different in kind - this tab
     * is holding credentials the server does not agree with - and still clears.
     */
    if (errorCode !== THEME_OWNER_MISMATCH) return;
    if (recoveredRef.current) return;
    // The one-shot is consumed only AFTER ownership is confirmed (A3 review, item 4). Setting it
    // first meant a mismatch that LOST its ownership race burned the recovery for the whole
    // provider lifetime, so the next genuine mismatch would do nothing at all.
    if (!owns(currentOwner())) return;
    recoveredRef.current = true;
    // No DOM application has happened (the layout effect needs a validated snapshot), and this
    // only ever ends the session it was started for.
    clearAuth();
  }, [errorCode, owns, currentOwner, clearAuth]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const saveMutation = useMutation<ThemeSaveResult, unknown, ThemeSaveInput, ThemeOwner>({
    // See `networkMode` on the query above: a paused mutation never reaches `mutationFn`, so the
    // deadline never starts and the editor's submit controls stay frozen on "Saving..." forever.
    networkMode: 'always',
    mutationFn: async (input) => {
      const owner = currentOwner();
      // Cancel older reads before the save, so a read that started earlier cannot land on top
      // of the value this save is about to confirm.
      await queryClient.cancelQueries({ queryKey });
      /**
       * CHECKED BEFORE THE WRITE, NOT ONLY AFTER IT (A3 review, item 1).
       *
       * `cancelQueries` is awaited, and an await is where the account can change. Checking only
       * after the response is the capture-then-await-then-check shape the wiring contract exists
       * to forbid: the PUT would have gone out under the REPLACEMENT session's credentials and
       * written this draft to the new account, and rejecting the response afterwards cannot
       * un-write a completed save. The post-response check below is kept - the round-trip is a
       * second await and needs its own guard.
       */
      if (!owns(owner)) {
        throw new ThemeError(THEME_NOT_OWNED, 'The theme save no longer owns this session.');
      }
      const operation: Operation = { settled: false };
      const response = await withDeadline(operation, inFlightRef.current, (signal) =>
        api.put<ThemeSaveResult>('/api/v1/admin/theme?clientVersion=2', input, {
          signal,
          callerActive: () => activeRef.current,
        }),
      );
      if (!owns(owner)) {
        throw new ThemeError(THEME_NOT_OWNED, 'The theme save no longer owns this session.');
      }
      return response.data;
    },
    // The owner is captured when the mutation STARTS and handed to `onSettled` as its context,
    // so the comparison below is against the tuple the save began under. Recomputing the owner
    // in the callback would compare the new session with itself and always agree.
    onMutate: (): ThemeOwner => currentOwner(),
    onSettled: (_data, _saveError, _input, owner) => {
      // ON SETTLEMENT, FETCH FRESH SERVER STATE — after success, after failure, and after a
      // write TIMEOUT, whose outcome is unknown by definition: the write may well have
      // committed, so the runtime asks the server rather than guessing, and never auto-retries
      // the write itself.
      if (!owner || !owns(owner)) return;
      void refresh();
    },
  });

  const reload = refresh;

  const mutateTheme = saveMutation.mutateAsync;
  const saveTheme = React.useCallback(
    (input: ThemeSaveInput): Promise<ThemeSaveResult> => mutateTheme(input),
    [mutateTheme],
  );

  // ── The account preference write (B2) ─────────────────────────────────────

  /**
   * One PATCH, already serialized by its caller, with the same ownership discipline as the admin
   * save: the owner is captured when the request STARTS, re-checked after every await, and a
   * request that no longer owns its session touches nothing at all — not the DOM, not the cache,
   * not this state.
   */
  const writePreference = React.useCallback(
    async (next: ThemePreference, sequence: number, owner: ThemeOwner): Promise<void> => {
      const isLatest = (): boolean => preferenceRequestRef.current === sequence;
      try {
        // Cancel older reads first, so a read that started before this write cannot land on top
        // of the value it is about to confirm. Ownership is re-checked AFTER this await — an
        // await is where the account can change, and the A3 review's item 1 is exactly this shape.
        await queryClient.cancelQueries({ queryKey });
        if (!owns(owner)) return;

        const operation: Operation = { settled: false };
        const response = await withDeadline(operation, inFlightRef.current, (signal) =>
          api.patch<PreferenceResult>(
            '/api/v1/settings/theme?clientVersion=2',
            // EXACTLY ONE KEY. The endpoint's schema is strict and the subject is always the
            // authenticated caller; sending a `userId` here is a 400 that writes nothing.
            { preference: next },
            { signal, callerActive: () => activeRef.current },
          ),
        );
        if (!owns(owner)) return;

        const result = response.data;
        const echoed = themePreferenceSchema.safeParse(result?.preference);
        if (!echoed.success || result.ownerUserId !== owner.userId) {
          throw new ThemeError(THEME_INVALID, 'The preference response did not match its schema.');
        }

        // COMMITTED. The two refs move BEFORE the cache write, because they are what a read
        // settling in between compares itself against.
        confirmedPreferenceRef.current = echoed.data;
        preferenceCommitRef.current += 1;
        queryClient.setQueryData<ThemeSnapshot>(queryKey, (current) =>
          current ? { ...current, preference: echoed.data } : current,
        );

        // An intermediate success of an overlapping sequence stops here: its value is confirmed
        // server state (recorded above), but a newer request owns what is painted and whether the
        // controls are still pending.
        if (!isLatest()) return;
        setPendingPreference(null);
        setPreferenceError(null);
        setPreferencePending(false);
      } catch (error) {
        if (!isLatest() || !owns(owner)) return;
        // ROLLBACK, in the same commit as the announcement: dropping the optimistic value returns
        // the painted mode to the confirmed server preference.
        setPendingPreference(null);
        setPreferenceError(describePreferenceError(error));
        setPreferencePending(false);
        // ON SETTLEMENT, ASK THE SERVER. A `THEME_TIMEOUT` means the outcome is UNKNOWN — the
        // write may well have committed — so the runtime never claims it did not and never
        // retries it; it re-reads. A refused write leaves the stored value alone, and re-reading
        // it costs one GET and removes the need to trust this rollback.
        void refresh();
      }
    },
    [queryClient, queryKey, owns, refresh],
  );

  const supportedModes = snapshot?.supportedModes;
  /**
   * THE CAPABILITY GATE. `setPreference` becomes non-null only once the server publishes a mode
   * beyond light — a version-1 answer (`['LIGHT']`) leaves it null and no picker renders, exactly
   * as in delivery A, including for a B bundle talking to a rolled-back A server.
   */
  const preferenceWritable = supportedModes?.some((supported) => supported !== 'LIGHT') === true;

  const setPreference = React.useMemo<PortalThemeContext['setPreference']>(() => {
    if (!preferenceWritable || !supportedModes) return null;
    /**
     * NEVER REJECTS. Every outcome is surfaced through `preferenceSave.error`, which is what the
     * picker announces; a rejected promise would add an unhandled rejection for every caller that
     * did not also duplicate that handling, and there would then be two places a failure is
     * reported from.
     */
    return async (next: ThemePreference): Promise<void> => {
      const sequence = preferenceRequestRef.current + 1;
      preferenceRequestRef.current = sequence;
      if (!supportedModes.includes(next)) {
        // REFUSED BEFORE ANY PATCH — an unsupported preference is never sent.
        setPendingPreference(null);
        setPreferencePending(false);
        setPreferenceError('That appearance option is not available in this release.');
        return;
      }
      const owner = currentOwner();
      // The optimistic paint happens at REQUEST time, not at PATCH time: the last thing the user
      // asked for is what the portal shows while the queue drains behind it.
      setPendingPreference(next);
      setPreferenceError(null);
      setPreferencePending(true);
      // SERIALIZED. The chain is the whole mechanism: two overlapping choices cannot interleave,
      // cannot reach the server out of order, and the last one requested is the last one written
      // — so the server's final value and the painted mode agree.
      const run = preferenceQueueRef.current.then(
        () => writePreference(next, sequence, owner),
        () => writePreference(next, sequence, owner),
      );
      preferenceQueueRef.current = run.then(
        () => undefined,
        () => undefined,
      );
      await run;
    };
  }, [preferenceWritable, supportedModes, currentOwner, writePreference]);

  // A new owner/generation/lifetime is a different account's preference. Nothing of the previous
  // one may be painted, announced or left pending.
  React.useEffect(() => {
    setPendingPreference(null);
    setPreferencePending(false);
    setPreferenceError(null);
    confirmedPreferenceRef.current = null;
  }, [queryKey]);

  const preferenceSave = React.useMemo(
    () => ({ pending: preferencePending, error: preferenceError }),
    [preferencePending, preferenceError],
  );

  /**
   * The snapshot the seam publishes carries the preference the runtime is CURRENTLY painting, so
   * the picker's checked option and the applied mode cannot disagree — and so no consumer needs
   * its own copy of the pending choice. The confirmed value stays in the query cache.
   */
  const exposedSnapshot = React.useMemo<ThemeSnapshot | undefined>(
    () => (snapshot ? { ...snapshot, preference } : undefined),
    [snapshot, preference],
  );

  const value = React.useMemo<PortalThemeContext | null>(
    () =>
      exposedSnapshot
        ? {
            snapshot: exposedSnapshot,
            mode,
            reload,
            saveTheme,
            preferenceSave,
            setPreference,
          }
        : null,
    [exposedSnapshot, mode, reload, saveTheme, preferenceSave, setPreference],
  );

  const signOut = React.useCallback(() => logout.mutate(), [logout]);

  // ── Cold: the portal is not revealed ──────────────────────────────────────
  if (!value) {
    const timedOut = errorCode === THEME_TIMEOUT;
    return (
      <div
        className="flex min-h-screen flex-col items-center justify-center gap-4 p-6"
        style={neutralSurface}
      >
        <div
          // KEYED so React mounts a NEW node when loading becomes failure. Swapping `role` and
          // `aria-live` on an element that is already in the accessibility tree is the shape
          // screen readers are least reliable about announcing; inserting a fresh live region
          // with its final role is the one they all handle.
          key={coldFailed ? 'theme-bootstrap-error' : 'theme-bootstrap-loading'}
          className="flex w-full max-w-md flex-col gap-4 rounded-md border p-6"
          style={neutralPanel}
          // Assertive: this replaces the whole application, so it is not a background status.
          role={coldFailed ? 'alert' : 'status'}
          aria-live={coldFailed ? 'assertive' : 'polite'}
        >
          {coldFailed ? (
            <>
              <p className="text-sm font-medium">
                {timedOut
                  ? 'Loading your workspace appearance took too long.'
                  : 'We could not load your workspace appearance.'}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  className={ACTION_CLASS}
                  style={neutralAction}
                  onClick={() => void refresh()}
                >
                  Retry
                </button>
                <button
                  type="button"
                  className={ACTION_CLASS}
                  style={neutralAction}
                  onClick={signOut}
                >
                  Sign out
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm font-medium">Loading your workspace…</p>
              {/* Sign out is available IMMEDIATELY: a bootstrap that never returns must not be
                  a locked door. */}
              <button
                type="button"
                className={ACTION_CLASS}
                style={neutralAction}
                onClick={signOut}
              >
                Sign out
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Warm: the last confirmed palette stays, one nonblocking error is announced ──
  return (
    <ThemeContext.Provider value={value}>
      {warmError ? (
        <p role="status" aria-live="polite" className="px-6 pt-3 text-xs text-content-secondary">
          Your saved appearance could not be refreshed. Showing the last version we loaded.
        </p>
      ) : null}
      {children}
      <PortalToaster />
    </ThemeContext.Provider>
  );
}
